import { Route, Routes } from "react-router-dom";
import { routes } from "~/application/theme/routes";
import { UserContext } from "~/application/types";
import { lazy, Suspense, useEffect } from "react";
import { AppLoading } from "~/containers/AppLoading/AppLoading";
import { AppContainer } from "~/presentation/core/containers/AppContainer";
import { SignInPage } from "../core/modules/Auth/pages/SignInPage";
import { RedefinePasswordPage } from "~/core/modules/Auth/pages/RedefinePasswordPage";
import { PreventAuth } from "./helpers/PreventAuth";
import { Redirect } from "./helpers/Redirect";
import { RequireAuth } from "./helpers/RequireAuth";
import { RequireRole } from "./helpers/RequireRole";
import { ChangeCustomerEmployeePasswordPage } from "~/core/modules/Customer/pages/ChangeCustomerEmployeePasswordPage";
import { InstantlyApprovalAuthPage } from "~/core/modules/Auth/pages/InstantlyApprovalAuthPage/InstantlyApprovalAuthPage";
import { PrivacyPolicyPage } from "~/core/modules/PrivacyPolicy/pages/PrivacyPolicyPage";
import { DashboardsBIPage } from "~/core/modules/Customer/pages/CustomerDashboardBIPage";
import { DashboardBIV2Page } from "~/core/modules/Customer/pages/CustomerDashboardBIV2Page";
import { VoucherPage } from "~/core/modules/Booking/modules/Vouchers/VoucherPage";
import OptionsMenuPage from "~/core/modules/Booking/modules/OptionsMenu/OptionsMenuPage";
import { SignUpPage } from "~/core/modules/SignUp/pages/SignUpPage";

const ConfigurationRouter = lazy(() =>
  import("./ConfigurationRouter").then((module) => ({ default: module.ConfigurationRouter })),
);

const DashboardRouter = lazy(() =>
  import("./DashboardRouter").then((module) => ({ default: module.DashboardRouter })),
);

const OrderRouter = lazy(() =>
  import("./OrderRouter").then((module) => ({ default: module.OrderRouter })),
);

const ExpenseRouter = lazy(() =>
  import("./ExpenseRouter").then((module) => ({ default: module.ExpenseRouter })),
);

const BookingRouter = lazy(() =>
  import("./BookingRouter").then((module) => ({ default: module.BookingRouter })),
);

const AgencyRouter = lazy(() =>
  import("./AgencyRouter").then((module) => ({ default: module.AgencyRouter })),
);

const ProfileRouter = lazy(() =>
  import("./ProfileRouter").then((module) => ({ default: module.ProfileRouter })),
);

const ReportingCenterRouter = lazy(() =>
  import("./ReportingCenterRouter").then((module) => ({ default: module.ReportingCenterRouter })),
);

const ExpenseCardsRouter = lazy(() =>
  import("./ExpenseCardsRouter").then((module) => ({ default: module.ExpenseCardsRouter })),
);

const NotificationsRouter = lazy(() =>
  import("./NotificationsRouter").then((module) => ({ default: module.NotificationsRouter })),
);

export const AppRouter = () => {
  useEffect(() => {
    import("./ConfigurationRouter");
    import("./DashboardRouter");
    import("./OrderRouter");
    import("./ExpenseRouter");
    import("./BookingRouter");
    import("./AgencyRouter");
    import("./ProfileRouter");
    import("./ReportingCenterRouter");
    import("./ExpenseCardsRouter");
  }, []);

  return (
    <Routes>
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/" element={<PreventAuth />}>
        <Route path="entrar" element={<SignInPage />} />
        <Route path="redefinir-senha" element={<RedefinePasswordPage />} />
        <Route path="senha-aprovar" element={<InstantlyApprovalAuthPage />} />
      </Route>

      <Route path="/" element={<RequireAuth />}>
        <Route path="cadastro" element={<RequireRole role={UserContext.Agency} />}>
          <Route index element={<SignUpPage />} />
        </Route>
        <Route path="*" element={<AppContainer />}>
          <Route
            path={routes.Configuration.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <ConfigurationRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.Orders.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <OrderRouter />
              </Suspense>
            }
          />

          <Route path="voucher">
            <Route index element={<VoucherPage />} />
          </Route>

          <Route path="menu-opcoes">
            <Route index element={<OptionsMenuPage />} />
          </Route>

          <Route
            path={routes.Booking.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <BookingRouter />
              </Suspense>
            }
          />

          <Route
            path={routes.Dashboard.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <DashboardRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.Expenses.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <ExpenseRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.Agencies.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <AgencyRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.Profile.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <ProfileRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.ReportingCenter.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <ReportingCenterRouter />
              </Suspense>
            }
          />

          <Route
            path={routes.DashboardBI.path}
            element={<RequireRole role={UserContext.Customer} />}
          >
            <Route index element={<DashboardsBIPage />} />
          </Route>

          <Route
            path={routes.DashboardBIV2.path}
            element={<RequireRole role={UserContext.Customer} />}
          >
            <Route index element={<DashboardBIV2Page />} />
          </Route>

          <Route
            path={routes.ExpenseCards.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <ExpenseCardsRouter />
              </Suspense>
            }
          />
          <Route
            path={routes.Notifications.route}
            element={
              <Suspense fallback={<AppLoading />}>
                <NotificationsRouter />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={null} />
      </Route>

      <Route path="/alterar-senha/:csrfToken" element={<ChangeCustomerEmployeePasswordPage />} />

      <Route index element={<Redirect />} />
    </Routes>
  );
};

AppRouter.displayName = "AppRouter";
